import React, { useMemo } from "react";
import { ActionIcon, Card, Group, Stack, Text, Tooltip } from "@mantine/core";
import { IconPencil, IconTrash } from "@tabler/icons-react";
import {
  PostAppointmentAttachment,
  PostAppointmentMessage,
  describeOffset,
} from "../../types/servicePostAppointment";
import AttachmentBadge from "./AttachmentBadge";

interface PostAppointmentMessageRowProps {
  message: PostAppointmentMessage;
  readOnly?: boolean;
  onEdit?: (message: PostAppointmentMessage) => void;
  onDelete?: (message: PostAppointmentMessage) => void;
}

function summarizeAttachments(
  attachments: PostAppointmentAttachment[],
): string {
  if (attachments.length === 0) return "No attachments";
  const docs = attachments.filter(
    (a) => a.source_type === "salon_document",
  ).length;
  const forms = attachments.length - docs;
  const parts: string[] = [];
  if (docs > 0) parts.push(`${docs} document${docs > 1 ? "s" : ""}`);
  if (forms > 0) parts.push(`${forms} form${forms > 1 ? "s" : ""}`);
  return parts.join(", ");
}

export const PostAppointmentMessageRow: React.FC<
  PostAppointmentMessageRowProps
> = ({ message, readOnly = false, onEdit, onDelete }) => {
  const headline =
    message.label?.trim() ||
    `Day ${message.offset_value}${message.offset_unit === "weeks" ? " (weeks)" : ""} message`;
  const offsetSummary = describeOffset(
    message.offset_value,
    message.offset_unit,
  );
  const attachmentSummary = useMemo(
    () => summarizeAttachments(message.attachments),
    [message.attachments],
  );
  const typeBadges = useMemo(() => {
    // Distinct source-type badges, capped at 3.
    const seen = new Set<string>();
    const list: PostAppointmentAttachment[] = [];
    for (const a of message.attachments) {
      if (!seen.has(a.source_type)) {
        seen.add(a.source_type);
        list.push(a);
      }
    }
    return list.slice(0, 3);
  }, [message.attachments]);

  return (
    <Card withBorder padding="sm" radius="sm">
      <Group justify="space-between" wrap="nowrap" gap="xs">
        <Stack gap={2} style={{ minWidth: 0 }}>
          <Group gap="xs" wrap="nowrap">
            <Text size="sm" fw={600} truncate>
              {headline}
            </Text>
            {typeBadges.map((a) => (
              <AttachmentBadge
                key={`${a.source_type}:${a.source_id}`}
                sourceType={a.source_type}
              />
            ))}
          </Group>
          <Text size="xs" c="dimmed">
            {offsetSummary} · {attachmentSummary}
          </Text>
        </Stack>
        {!readOnly && (
          <Group gap={4} wrap="nowrap">
            <Tooltip label="Edit">
              <ActionIcon
                variant="subtle"
                size="sm"
                onClick={() => onEdit?.(message)}
                aria-label="Edit post-appointment message"
              >
                <IconPencil size={14} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Delete">
              <ActionIcon
                variant="subtle"
                color="red"
                size="sm"
                onClick={() => onDelete?.(message)}
                aria-label="Delete post-appointment message"
              >
                <IconTrash size={14} />
              </ActionIcon>
            </Tooltip>
          </Group>
        )}
      </Group>
    </Card>
  );
};

export default PostAppointmentMessageRow;
